/**
 * Writeoffs Dual-Write Test
 *
 * Tests that writeoff operations in Firestore are properly synced to Supabase.
 *
 * Test variations:
 * W1: Create single product writeoff
 * W2: Delete writeoff
 */

import {
  saveNewWriteOff,
  deleteWriteOff,
} from '../global/Firebase/inventory';
import { api } from '../global/API/apiClient';
import { getDefaultStore } from 'jotai';
import { organizationAtom, storesDictAtom, productsDictAtom, dbAtom } from '../global/AtomsProvider';

// Test variation definitions
export const WRITEOFF_TEST_VARIATIONS = [
  { id: 'W1', category: 'create', name: 'Create single product writeoff' },
  { id: 'W2', category: 'delete', name: 'Delete writeoff' },
];

// Get test categories for UI grouping
export function getWriteoffTestCategories() {
  return [
    { id: 'create', name: 'Create Writeoffs', variations: ['W1'] },
    { id: 'delete', name: 'Delete Writeoff', variations: ['W2'] },
  ];
}

// Helper to normalize values for comparison
function normalizeValue(val) {
  if (val === undefined || val === null) return null;
  if (typeof val === 'number') return val;
  if (typeof val === 'string') {
    const num = parseFloat(val);
    if (!isNaN(num)) return num;
    return val.trim();
  }
  return val;
}

// Helper to compare values with tolerance for floating point
function valuesMatch(a, b, tolerance = 0.01) {
  const normA = normalizeValue(a);
  const normB = normalizeValue(b);

  if (normA === normB) return true;
  if (normA === null || normB === null) return normA === normB;

  // Numeric comparison with tolerance
  if (typeof normA === 'number' && typeof normB === 'number') {
    return Math.abs(normA - normB) < tolerance;
  }

  return normA === normB;
}

/**
 * Get test data based on atoms
 */
export function getWriteoffTestData() {
  const jotaiStore = getDefaultStore();
  const organization = jotaiStore.get(organizationAtom);
  const storesDict = jotaiStore.get(storesDictAtom);
  const productsDict = jotaiStore.get(productsDictAtom);
  const db = jotaiStore.get(dbAtom);

  if (!organization || !db) {
    return null;
  }

  // Get first store
  const storeEntries = Object.entries(storesDict || {});
  if (storeEntries.length < 1) {
    return null;
  }

  const store = { id: storeEntries[0][0], ...storeEntries[0][1] };

  // Get first product for writeoff test
  const productEntries = Object.entries(productsDict || {});
  if (productEntries.length < 1) {
    return null;
  }

  const testProduct = {
    id: productEntries[0][0],
    ...productEntries[0][1],
    ref: db.collection('products').doc(productEntries[0][0]),
  };

  return {
    organization,
    store,
    db,
    organizationId: organization.id,
    testProduct,
  };
}

/**
 * Compare writeoff via backend API
 */
async function compareWriteoffViaApi(writeoffId, writeoffData, log) {
  try {
    const response = await api.get(`/writeoffs/compare/${writeoffId}`);
    const apiResult = response.data;

    if (!apiResult.exists) {
      return {
        writeoffId,
        foundInSupabase: false,
        matches: false,
        differences: [{
          field: 'writeoff',
          supabaseValue: null,
          firestoreValue: 'exists',
          severity: 'critical',
        }],
        warnings: [],
      };
    }

    const supabaseData = apiResult.supabaseData;
    const differences = [];

    // Compare key fields
    const fieldsToCompare = [
      { fs: 'totalSum', sb: 'total_amount' },
    ];

    for (const { fs, sb } of fieldsToCompare) {
      const fsVal = normalizeValue(writeoffData?.[fs]);
      const sbVal = normalizeValue(supabaseData?.[sb]);

      if (!valuesMatch(fsVal, sbVal)) {
        differences.push({
          field: sb,
          supabaseValue: sbVal,
          firestoreValue: fsVal,
          severity: 'critical',
        });
      }
    }

    // Compare items count
    const fsItemsCount = writeoffData?.items?.length || 1; // Usually single product
    const sbItemsCount = supabaseData?.items?.length || 0;
    if (fsItemsCount !== sbItemsCount) {
      differences.push({
        field: 'items_count',
        supabaseValue: sbItemsCount,
        firestoreValue: fsItemsCount,
        severity: 'warning',
      });
    }

    return {
      writeoffId,
      foundInSupabase: true,
      matches: differences.filter(d => d.severity === 'critical').length === 0,
      differences,
      warnings: differences.filter(d => d.severity === 'warning').map(d => d.field),
      supabaseData,
    };
  } catch (error) {
    log(`API comparison error: ${error.message}`);
    return {
      writeoffId,
      foundInSupabase: false,
      matches: false,
      differences: [{
        field: 'api_error',
        supabaseValue: null,
        firestoreValue: error.message,
        severity: 'critical',
      }],
      warnings: [`API error: ${error.message}`],
    };
  }
}

/**
 * Check if writeoff was deleted from Supabase
 */
async function checkWriteoffDeleted(writeoffId, maxRetries = 3) {
  for (let i = 0; i < maxRetries; i++) {
    try {
      const response = await api.get(`/writeoffs/compare/${writeoffId}`);
      if (!response.data.exists) {
        return true;
      }
      // Wait before retry
      await new Promise(resolve => setTimeout(resolve, 2000));
    } catch (error) {
      // 404 means deleted
      if (error.response?.status === 404) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Run a single writeoff test
 */
async function runSingleTest(variationId, testData, log) {
  const { db, store, organizationId, testProduct } = testData;

  let createdRef = null;
  let createdWriteoffData = null;
  let result = {
    variationId,
    passed: false,
    error: null,
    comparisonResult: null,
  };

  try {
    switch (variationId) {
      case 'W1': {
        // Create single product writeoff
        const timestamp = Date.now();

        if (!testProduct) {
          result.error = 'No test product available';
          break;
        }

        const quantity = 1;
        const purchasePrice = testProduct.purchasePrice || 100;
        const totalSum = quantity * purchasePrice;

        const writeoffData = {
          organizationId,
          store: store.id,
          product: {
            id: testProduct.id,
            name: testProduct.name || 'Test Product',
            purchasePrice,
            isComposite: false,
            ref: testProduct.ref,
          },
          quantity,
          totalSum,
          date: new Date(),
          dailyStatID: `test-writeoff-${timestamp}-${store.id}`,
          reason: { id: 'test', text: 'Dual-write test' },
        };

        log(`Creating writeoff: ${testProduct.name}, qty ${quantity}, total ${totalSum}`);
        createdRef = await saveNewWriteOff(db, writeoffData);
        createdWriteoffData = { ...writeoffData, ref: createdRef };

        log('Waiting 6000ms for Supabase sync...');
        await new Promise(resolve => setTimeout(resolve, 6000));

        // Compare
        const comparison = await compareWriteoffViaApi(createdRef.id, writeoffData, log);
        result.comparisonResult = comparison;
        result.passed = comparison.foundInSupabase;

        // Cleanup
        log('Cleaning up test writeoff...');
        await deleteWriteOff(db, createdRef, createdWriteoffData);
        await new Promise(resolve => setTimeout(resolve, 2000));

        break;
      }

      case 'W2': {
        // Delete writeoff test
        const timestamp = Date.now();

        if (!testProduct) {
          result.error = 'No test product available';
          break;
        }

        const quantity = 1;
        const purchasePrice = testProduct.purchasePrice || 50;
        const totalSum = quantity * purchasePrice;

        const writeoffData = {
          organizationId,
          store: store.id,
          product: {
            id: testProduct.id,
            name: testProduct.name || 'Test Product',
            purchasePrice,
            isComposite: false,
            ref: testProduct.ref,
          },
          quantity,
          totalSum,
          date: new Date(),
          dailyStatID: `test-writeoff-del-${timestamp}-${store.id}`,
          reason: { id: 'test', text: 'Dual-write delete test' },
        };

        log('Creating writeoff to delete...');
        createdRef = await saveNewWriteOff(db, writeoffData);
        createdWriteoffData = { ...writeoffData, ref: createdRef };

        log('Waiting 6000ms for Supabase sync...');
        await new Promise(resolve => setTimeout(resolve, 6000));

        // Verify it exists first
        const createCheck = await compareWriteoffViaApi(createdRef.id, writeoffData, log);
        if (!createCheck.foundInSupabase) {
          result.error = 'Writeoff was not created in Supabase';
          break;
        }

        // Now delete
        log('Deleting writeoff...');
        await deleteWriteOff(db, createdRef, createdWriteoffData);

        log('Waiting 6000ms for delete sync...');
        await new Promise(resolve => setTimeout(resolve, 6000));

        // Check if deleted
        const deleted = await checkWriteoffDeleted(createdRef.id);
        result.passed = deleted;
        result.comparisonResult = {
          writeoffId: createdRef.id,
          foundInSupabase: !deleted,
          matches: deleted,
          differences: deleted ? [] : [{
            field: 'deleted',
            supabaseValue: 'still exists',
            firestoreValue: 'deleted',
            severity: 'critical',
          }],
        };

        break;
      }

      default:
        result.error = `Unknown variation: ${variationId}`;
    }
  } catch (error) {
    result.error = error.message;
    log(`Error: ${error.message}`);

    // Cleanup on error
    if (createdWriteoffData && createdRef) {
      try {
        await deleteWriteOff(db, createdRef, createdWriteoffData);
      } catch (cleanupError) {
        log(`Cleanup failed: ${cleanupError.message}`);
      }
    }
  }

  return result;
}

/**
 * Run all writeoff tests
 */
export async function runFullWriteoffTest(log = console.log, selectedVariations = null) {
  log('Starting writeoff dual-write tests...');

  const testData = getWriteoffTestData();
  if (!testData) {
    log('ERROR: Could not get test data. Need at least 1 store and to be logged in.');
    return { passed: 0, failed: 0, skipped: 0, results: [] };
  }

  if (!testData.testProduct) {
    log('ERROR: Need at least 1 product to run writeoff tests.');
    return { passed: 0, failed: 0, skipped: 0, results: [] };
  }

  log('\n========================================');
  log('WRITEOFFS DUAL-WRITE TEST SUITE');
  log('========================================');
  log(`Organization: ${testData.organization.name || testData.organizationId}`);
  log(`Store: ${testData.store.name || testData.store.id}`);
  log(`Test Product: ${testData.testProduct.name || testData.testProduct.id}`);
  log('========================================\n');

  const variationsToRun = selectedVariations || WRITEOFF_TEST_VARIATIONS.map(v => v.id);
  const results = [];
  let passed = 0;
  let failed = 0;
  let skipped = 0;

  for (const variationId of variationsToRun) {
    const variation = WRITEOFF_TEST_VARIATIONS.find(v => v.id === variationId);
    if (!variation) {
      log(`Skipping unknown variation: ${variationId}`);
      skipped++;
      continue;
    }

    log(`\n--- Running test ${variationId}: ${variation.name} ---`);

    const result = await runSingleTest(variationId, testData, log);
    results.push(result);

    if (result.error) {
      log(`ERROR: ${result.error}`);
      failed++;
    } else if (result.passed) {
      log(`PASS: ${variation.name}`);
      passed++;
    } else {
      log(`FAIL: ${variation.name}`);
      if (result.comparisonResult?.differences?.length > 0) {
        log('Differences: ' + JSON.stringify(result.comparisonResult.differences, null, 2));
      }
      failed++;
    }
  }

  log('\n========================================');
  log('TEST SUMMARY');
  log('========================================');
  log(`Passed: ${passed}`);
  log(`Failed: ${failed}`);
  log(`Skipped: ${skipped}`);
  log('========================================');

  if (failed > 0) {
    log('\nFAILED TESTS:');
    results.filter(r => !r.passed && !r.error).forEach(r => {
      const variation = WRITEOFF_TEST_VARIATIONS.find(v => v.id === r.variationId);
      log(`  - ${r.variationId}: ${variation?.name}`);
      r.comparisonResult?.differences?.forEach(d => {
        log(`    ${d.field}: Supabase="${d.supabaseValue}" vs Firestore="${d.firestoreValue}"`);
      });
    });
  }

  return { passed, failed, skipped, results };
}

export default {
  WRITEOFF_TEST_VARIATIONS,
  getWriteoffTestCategories,
  getWriteoffTestData,
  runFullWriteoffTest,
};
